import React from 'react';
import { Target, Heart, Award, TrendingUp, Users, Clock } from 'lucide-react';
import { Card } from '../components/ui/card';
import { MobileCarousel } from '../components/MobileCarousel';

export const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-[#58225a] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1686100510109-d520e59bf0ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsYXclMjBvZmZpY2UlMjB3b3Jrc3BhY2V8ZW58MHx8fHwxNzc2Mjg0ODc5fDA&ixlib=rb-4.1.0&q=85" alt="Modern office space" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#58225a]/80"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">About Trademark Examining Lawyer</h1>
            <p className="text-xl md:text-2xl text-gray-300">
              Experienced professionals dedicated to helping businesses secure and protect their intellectual property rights with confidence.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-[#58225a] mb-8 text-center">Our Story</h2>
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
              <p>Trademark Examining Lawyer was established with a clear mission: to make trademark protection more accessible, affordable, and understandable for businesses of all sizes. Many entrepreneurs delayed protecting their brands due to complicated legal processes or expensive traditional law firms. We created a better solution.</p>
              <p>Our team combines trademark attorneys, intellectual property specialists, and client support professionals who work together to deliver reliable legal services without unnecessary complexity. By streamlining the trademark process, we help startups, growing companies, and established organizations secure their valuable brand assets efficiently.</p>
              <p>Today, we proudly assist clients throughout the United States across a wide range of industries. Whether you're protecting your first trademark or managing multiple brand registrations, our goal remains the same—providing dependable legal support and a seamless client experience from start to finish.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#58225a] mb-4">The Principles That Guide Our Work</h2>
            <p className="text-lg text-gray-600">These core values influence every service we provide and every client relationship we build.</p>
          </div>
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {[
              { icon: Target, t: "Accuracy", d: "Every trademark application is carefully prepared to meet USPTO requirements and maximize filing accuracy." },
              { icon: Heart, t: "Client Focus", d: "We understand the hard work, dedication, and vision behind every brand we help protect." },
              { icon: Award, t: "Transparency", d: "We provide honest feedback about trademark eligibility and potential challenges before moving forward." },
              { icon: TrendingUp, t: "Efficiency", d: "Our streamlined systems and organized workflows help reduce delays and keep projects moving smoothly." }
            ].map((v, i) => (
              <Card key={i} className="p-8 text-center hover:shadow-xl transition-shadow">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary text-white mb-6"><v.icon className="h-8 w-8" /></div>
                <h3 className="text-xl font-bold text-[#58225a] mb-4">{v.t}</h3>
                <p className="text-gray-600">{v.d}</p>
              </Card>
            ))}
          </div>
          <MobileCarousel>
            {[
              { icon: Target, t: "Accuracy", d: "Every trademark application is carefully prepared to meet USPTO requirements and maximize filing accuracy." },
              { icon: Heart, t: "Client Focus", d: "We understand the hard work, dedication, and vision behind every brand we help protect." },
              { icon: Award, t: "Transparency", d: "We provide honest feedback about trademark eligibility and potential challenges before moving forward." },
              { icon: TrendingUp, t: "Efficiency", d: "Our streamlined systems and organized workflows help reduce delays and keep projects moving smoothly." }
            ].map((v, i) => (
              <Card key={i} className="p-8 text-center shadow-lg">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary text-white mb-6"><v.icon className="h-8 w-8" /></div>
                <h3 className="text-xl font-bold text-[#58225a] mb-4">{v.t}</h3>
                <p className="text-gray-600">{v.d}</p>
              </Card>
            ))}
          </MobileCarousel>
        </div>
      </section>

      {/* Approach */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-[#58225a] mb-8 text-center">Our Commitment to Client Success</h2>
            <div className="space-y-8">
              {[
                { t: "Honest Evaluations", d: "Before filing begins, we assess your trademark and provide a straightforward analysis of its strengths, potential concerns, and registration prospects. This helps you make informed decisions from the start." },
                { t: "Specialized Trademark Knowledge", d: "Trademark law is our primary focus. This concentration allows our legal team to stay current with USPTO procedures, examiner expectations, and industry developments." },
                { t: "Modern Technology Solutions", d: "Secure digital tools allow us to manage applications efficiently while giving clients visibility into every stage of the registration process." },
                { t: "Ongoing Brand Protection", d: "Our relationship does not end after registration. We provide continued monitoring, renewal assistance, and support designed to keep your trademark protected long-term." }
              ].map((a, i) => (
                <div key={i}>
                  <h3 className="text-2xl font-bold text-[#58225a] mb-4">{a.t}</h3>
                  <p className="text-lg text-gray-700 leading-relaxed">{a.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="relative py-20 text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1767342007060-694d6a7db73f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMGNpdHlzY2FwZSUyMHNreWxpbmUlMjBuaWdodCUyMGRhcmt8ZW58MHx8fHwxNzc2Mjg2OTQzfDA&ixlib=rb-4.1.0&q=85" alt="City skyline at night" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#58225a]/85"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">Our Mission</h2>
            <p className="text-xl leading-relaxed text-gray-300">
              To help businesses confidently protect their brands by providing accessible, professional, and dependable trademark services. We strive to remove unnecessary barriers and simplify the path to intellectual property protection.
            </p>
          </div>
        </div>
      </section>

      {/* What Sets Us Apart */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="w-full">
              <img src="https://images.unsplash.com/photo-1686100510242-2521b6f89a68?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxjb3Jwb3JhdGUlMjBvZmZpY2UlMjBpbnRlcmlvciUyMGRlc2lnbnxlbnwwfHx8fDE3NzYyODQ4OTZ8MA&ixlib=rb-4.1.0&q=85" alt="Modern corporate interior" className="w-full h-auto rounded-2xl shadow-2xl" />
            </div>
            <div className="w-full">
              <h2 className="text-4xl font-bold text-[#58225a] mb-6">Why Businesses Choose Trademark Examining Lawyer</h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">Among many trademark service providers, Trademark Examining Lawyer stands apart through responsive support, legal experience, and a commitment to delivering exceptional value.</p>
              <div className="space-y-6">
                {[
                  { icon: Award, t: "Trademark-Focused Experience", d: "Intellectual property protection is our specialty. This focused approach allows us to provide knowledgeable guidance throughout the registration process." },
                  { icon: Users, t: "Dedicated Support Specialists", d: "Every client works with a dedicated representative who understands their goals and provides personalized assistance." },
                  { icon: Clock, t: "Simplified Processes", d: "Digital onboarding, streamlined communication, and efficient filing procedures help save valuable time." }
                ].map((item, i) => (
                  <div key={i} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary flex items-center justify-center"><item.icon className="h-6 w-6 text-white" /></div>
                    <div>
                      <h3 className="text-xl font-bold text-[#58225a] mb-2">{item.t}</h3>
                      <p className="text-gray-600">{item.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Numbers */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#58225a] mb-4">Results That Speak</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Trusted by businesses across diverse market sectors.</p>
          </div>
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {[
              { v: "100+", l: "Industries Served", d: "Trusted by businesses across diverse market sectors." },
              { v: "98%", l: "Success Percentage", d: "Delivering reliable trademark solutions with proven results." },
              { v: "30+", l: "Professionals", d: "Experienced specialists dedicated to protecting your brand." },
              { v: "24/7", l: "Customer Assistance", d: "Support available whenever you need expert guidance." }
            ].map((s, i) => (
              <Card key={i} className="p-8 text-center hover:shadow-xl transition-shadow">
                <div className="text-5xl font-bold text-[#e8a8c8] mb-2">{s.v}</div>
                <div className="text-[#58225a] font-semibold mb-2">{s.l}</div>
                <p className="text-gray-600 text-sm">{s.d}</p>
              </Card>
            ))}
          </div>
          <MobileCarousel autoPlayInterval={3500}>
            {[
              { v: "100+", l: "Industries Served", d: "Trusted by businesses across diverse market sectors." },
              { v: "98%", l: "Success Percentage", d: "Delivering reliable trademark solutions with proven results." },
              { v: "30+", l: "Professionals", d: "Experienced specialists dedicated to protecting your brand." },
              { v: "24/7", l: "Customer Assistance", d: "Support available whenever you need expert guidance." }
            ].map((s, i) => (
              <Card key={i} className="p-8 text-center shadow-lg">
                <div className="text-5xl font-bold text-[#e8a8c8] mb-2">{s.v}</div>
                <div className="text-[#58225a] font-semibold mb-2">{s.l}</div>
                <p className="text-gray-600 text-sm">{s.d}</p>
              </Card>
            ))}
          </MobileCarousel>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="w-full">
              <h2 className="text-4xl font-bold text-[#58225a] mb-6">A Simple Process Built Around Your Needs</h2>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">We keep clients informed throughout every stage of the trademark journey.</p>
              <div className="space-y-6">
                {[
                  { t: "Initial Consultation", d: "We learn about your business, brand objectives, and protection requirements." },
                  { t: "Trademark Research", d: "Our specialists conduct detailed searches and identify potential conflicts before filing." },
                  { t: "Application Preparation", d: "Attorneys prepare and submit your application accurately and electronically with the USPTO." },
                  { t: "Ongoing Support", d: "We monitor progress, respond to examiner inquiries, and keep you informed of important updates." }
                ].map((step, i) => (
                  <div key={i} className="border-l-4 border-[#e8a8c8] pl-6">
                    <h3 className="text-xl font-bold text-[#58225a] mb-2">{step.t}</h3>
                    <p className="text-gray-600">{step.d}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="w-full">
              <img src="https://images.unsplash.com/photo-1758519288417-d359ac3c494d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxsZWdhbCUyMHRlYW0lMjBjb2xsYWJvcmF0aW9uJTIwcHJvZmVzc2lvbmFsc3xlbnwwfHx8fDE3NzYyODQ4Nzl8MA&ixlib=rb-4.1.0&q=85" alt="Two professionals discussing over coffee" className="w-full h-auto rounded-2xl shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Commitment */}
      <section className="py-20 bg-[#f5f7eb]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-[#58225a] mb-8 text-center">Promises We Make to Every Client</h2>
            <div className="hidden md:grid md:grid-cols-2 gap-6">
              {[
                { t: "Clear Communication", d: "We explain legal concepts in straightforward language so you always understand the status of your application." },
                { t: "Honest Guidance", d: "If concerns exist regarding your trademark, we discuss them openly and recommend practical solutions." },
                { t: "Prompt Responses", d: "When issues arise, our legal team acts quickly to address them and keep your application progressing." },
                { t: "Long-Term Partnership", d: "As your company grows, we help expand and strengthen your trademark protection strategy to support future success." }
              ].map((c, i) => (
                <Card key={i} className="p-6 hover:shadow-lg transition-shadow">
                  <h3 className="text-xl font-bold text-[#58225a] mb-3">{c.t}</h3>
                  <p className="text-gray-600">{c.d}</p>
                </Card>
              ))}
            </div>
            <MobileCarousel>
              {[
                { t: "Clear Communication", d: "We explain legal concepts in straightforward language so you always understand the status of your application." },
                { t: "Honest Guidance", d: "If concerns exist regarding your trademark, we discuss them openly and recommend practical solutions." },
                { t: "Prompt Responses", d: "When issues arise, our legal team acts quickly to address them and keep your application progressing." },
                { t: "Long-Term Partnership", d: "As your company grows, we help expand and strengthen your trademark protection strategy to support future success." }
              ].map((c, i) => (
                <Card key={i} className="p-6 shadow-lg">
                  <h3 className="text-xl font-bold text-[#58225a] mb-3">{c.t}</h3>
                  <p className="text-gray-600">{c.d}</p>
                </Card>
              ))}
            </MobileCarousel>
          </div>
        </div>
      </section>
    </div>
  );
};
